import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {merge} from 'lodash-es';
import {SnackService} from "../../../services/snack.service";
import {ApiResponse} from "../../../classes/api-response";
import {Contract} from "../../../model/contract";
import {Company} from "../../../model/company";
import {CompaniesService} from "../../../services/companies.service";
import {AuthService} from "../../../services/auth.service";
import {Constants} from "../../../utils/constants";
import {Testbed} from "../../../model/testbed";
import {TestbedsService} from "../../../services/testbeds.service";
import {Box} from "../../../model/box";
import {BoxesService} from "../../../services/boxes-service";
import {PatchContract} from "../../../model/patch-contract";
import {MatDialog} from "@angular/material/dialog";
import {CreateTestbedDialogComponent} from "../../testbeds/create-testbed-dialog/create-testbed-dialog.component";
import {MatRadioChange} from "@angular/material/radio";
import {Observable, of} from "rxjs";
import {map, switchMap, tap} from "rxjs/operators";
import {DialogsService} from "../../../services/dialogs.service";
import {CreateStorageDialogComponent} from "../../storage/create-storage-dialog/create-storage-dialog.component";
import {Storage} from "../../../model/storage";
import {hasPermission, User} from "../../../model/user";
import {ContractsService} from "../../../services/contracts.service";
import {UsersService} from "../../../services/users.service";

@Component({
  selector: 'aw-contract-form',
  templateUrl: './contract-form.component.html',
  styleUrls: ['./contract-form.component.scss']
})
export class ContractFormComponent implements OnInit {

  contractForm: UntypedFormGroup;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();
  generalError?: ApiResponse;

  contractStatuses = Constants.contractStatuses;
  boxStatuses = Constants.boxStatuses;
  companies: Company[] = [];
  customers: User[] = [];
  researchers: User[] = [];
  alwaysContainsAllTestbeds: Testbed[] = [];
  filteredTestbeds: Testbed[] = [];
  boxes: Box[] = [];
  loggedUser: User;
  loadingCompanies = false;
  loadingCustomers = false;
  loadingTestbeds = false;
  adminHasSelectedCompany = false;
  isEditing = false;
  saving = false;
  selectedCustomer: User | undefined = new User();
  selectedCompany: Company | undefined = new Company();
  selectedTestbed: Testbed | undefined = new Testbed();
  validatorsForUserRole: ValidatorFn[] = [];
  validatorForStatus: ValidatorFn[] = [];
  testbedReferentControls: AbstractControl[] = [];
  iAmCustomer = false;
  iAmAdmin = false;

  testbedReferentFieldsHidden = false;
  minDate?: string;
  entities = Constants.entities;
  contractTypes = Constants.contractTypes;
  general = Constants.general;

  customers$!: Observable<User[]>;

  contractType = this.contractTypes.directContract;

  constructor(
    private companiesService: CompaniesService,
    private usersService: UsersService,
    private contractService: ContractsService,
    private authService: AuthService,
    private testbedsService: TestbedsService,
    private boxesService: BoxesService,
    private snackService: SnackService,
    private changeDecRef: ChangeDetectorRef,
    public dialog: MatDialog,
    public dialogService: DialogsService
  ) {


    this.contractForm = new UntypedFormGroup(
      {
        contractType: new UntypedFormControl(
          null,
          []
        ),
        isSameReferent: new UntypedFormControl(
          null,
          []
        ),
        title: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        description: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        startDate: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        endDate: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        extendedEndDate: new UntypedFormControl(
          '',
          []
        ),
        note: new UntypedFormControl(
          null,
          []
        ),
        numberOfBoxes: new UntypedFormControl(
          '',
          [
            Validators.required,
            Validators.min(this.general.minimumNumberOfBoxesValue),
            Validators.max(this.general.maximumNumberOfBoxesValue),
          ]
        ),
        thirdPartyContract: new UntypedFormControl(
          null,
          []
        ),
        company: new UntypedFormControl(
          null,
          Validators.required
        ),
        testbed: new UntypedFormControl(
          null,
          []
        ),
        contractReferent: new UntypedFormControl(
          null,
          Validators.required
        ),
        testbedReferentFirstName: new UntypedFormControl(
          null,
          [
            Validators.required
          ]
        ),
        testbedReferentLastName: new UntypedFormControl(
          null,
          [
            Validators.required
          ]
        ),
        testbedReferentEmail: new UntypedFormControl(
          null,
          [
            Validators.required,
            Validators.pattern("[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?")
          ]
        ),
        testbedReferentPhoneNumber: new UntypedFormControl(
          null,
          [
            Validators.required,
            Validators.pattern('[- +()0-9]+')
          ]
        ),
      },
    );

    this.testbedReferentControls.push(
      this.contractForm.controls['testbedReferentFirstName'],
      this.contractForm.controls['testbedReferentLastName'],
      this.contractForm.controls['testbedReferentEmail'],
      this.contractForm.controls['testbedReferentPhoneNumber']
    );
  }

  _contract = new Contract();

  @Input() set contract(value: Contract) {
    this._contract = merge(new Contract(), value);
    this.selectedTestbed = value.testbed;
    this.selectedCompany = this.iAmCustomer && !this.iAmAdmin ? this.loggedUser.company : value.company;
    this.selectedCustomer = this.iAmCustomer && !this.iAmAdmin ? this.loggedUser : value.contractReferent;
    this.isEditing = !!this._contract.id;
    this.contractForm.patchValue(this._contract);

    if (value.thirdPartyContract)
      this.contractType = this.contractTypes.partnership;

    if (value.status !== undefined)
      this.clearTestbedReferentValidators();

  }

  ngOnInit(): void {

    this.customers$ =
      of(true).pipe(
        tap(() => this.loadingCustomers = true),
        switchMap(() => this.usersService.getAllUsersByCompany(this.selectedCompany!.id)),
        // fixme: it may be a weak check:
        //  api now returns all users
        map((value: User[]) => value.filter(u => hasPermission(u, 'customer'))),
        tap((x) => {
          this.customers = x;
          this.loadingCustomers = false;
        })
      );

    this.loggedUser = this.authService.loggedUser();
    this.iAmCustomer = hasPermission(this.loggedUser, 'customer');
    this.iAmAdmin = hasPermission(this.loggedUser, 'admin');
    
    this.contractForm.controls['contractType'].setValue(this.contractTypes.directContract);
    this.setContractType({  value: this.contractTypes.directContract  } as MatRadioChange);


    if (this._contract.extendedEndDate)
      this.minDate = new Date(this._contract.extendedEndDate).toISOString();
    else if (this._contract.endDate)
      this.minDate = new Date(this._contract.endDate).toISOString();

    if (this._contract.status === this.contractStatuses.ongoing)
      this.validatorForStatus.push(Validators.required);


    if (this.iAmAdmin) {
      this.validatorsForUserRole.push(Validators.required);
    } else {
      this.selectedCompany = this.loggedUser.company;
      this.selectedCustomer = this.loggedUser;
      this.contractForm.controls['company'].setValue(this.loggedUser.company);
      this.contractForm.controls['contractReferent'].setValue(this.loggedUser);
    }

    // if i am creating as an admin
    if (!this.isEditing && this.iAmAdmin) {
      this.loadingTestbeds = true;
      this.loadingCompanies = true;
      this.adminHasSelectedCompany = false;

      this.companiesService.getAllCompanies().subscribe(companies => {
        this.companies = companies;
        this.loadingCompanies = false;
      });

      this.testbedsService.getAllTestbeds().subscribe(testbeds => {
        this.alwaysContainsAllTestbeds = testbeds;
        testbeds = this.filterBusyTestbeds(testbeds);
        this.filteredTestbeds = testbeds;
        this.loadingTestbeds = false;
      });

    }

    if (this.iAmCustomer && !this.iAmAdmin && (this._contract.status === this.contractStatuses.new || this._contract.status === this.contractStatuses.testbedAssociation || this._contract.status === undefined)) {
      this.loadingTestbeds = true;
      this.companies.push(this.loggedUser.company);

      this.testbedsService.getAllTestbedsByCompany(this.loggedUser.company.id).subscribe(testbeds => {
        this.alwaysContainsAllTestbeds = testbeds;
        testbeds = this.filterBusyTestbeds(testbeds);
        this.filteredTestbeds = testbeds;

        this.contractForm.controls["testbed"].setValue(this.filteredTestbeds.find(testbed => testbed.id === this._contract.testbed?.id));
        this.loadingTestbeds = false;
        this.changeDecRef.detectChanges();
      });
    }

    if (this.iAmAdmin && this._contract.company && (this._contract.status === this.contractStatuses.new || this._contract.status === undefined)) {

      this.loadingTestbeds = true;

      this.testbedsService.getAllTestbedsByCompany(this._contract.company.id).subscribe(testbeds => {
        this.alwaysContainsAllTestbeds = testbeds;
        testbeds = this.filterBusyTestbeds(testbeds);
        this.filteredTestbeds = testbeds;
        this.contractForm.controls["testbed"].setValue(this.filteredTestbeds.find(testbed => testbed.id === this._contract.testbed?.id));
        this.loadingTestbeds = false;
        this.changeDecRef.detectChanges();
      });

    }

    this.changeDecRef.detectChanges();

  }

  getSixMonthsAfterStartDate(): Date {

    const startDateValue: Date = this.contractForm.controls['startDate'].value;

    if (startDateValue) {
      const startDateInMillis = new Date(startDateValue).getTime();
      const date = new Date(startDateInMillis + Constants.general.sixMonthsInMillis + Constants.general.oneDayInMillis);
      date.setHours(12);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
      return date;
    }

    return new Date(Date.now() + Constants.general.sixMonthsInMillis);

  }

  getSixMonthsBeforeEndDate(): Date | undefined {

    const endDateValue: Date = this.contractForm.controls['endDate'].value;

    if (endDateValue) {
      const endDateInMillis = new Date(endDateValue).getTime();
      const date = new Date(endDateInMillis - Constants.general.sixMonthsInMillis);
      date.setHours(12);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
      return date;
    }

    return undefined;

  }

  openTestbedForm(): void {
    const selectedCompany = this.contractForm.controls['company'].value;
    this.dialog.open(CreateTestbedDialogComponent, {data: {creatingFromContract: true, company: selectedCompany}})
      .afterClosed()
      .subscribe(
        testbed => {
          this.contractForm.controls["testbed"].setValue(testbed);
          this.selectedTestbed = testbed;
          if (this.selectedTestbed) {
            this.alwaysContainsAllTestbeds.push(this.selectedTestbed);
            this.filterTestbedsForCompany();
          }
          this.changeDecRef.detectChanges();
        });
  }
  onCompanyChange(): void {
    this.selectedTestbed = undefined;
    this.selectedCustomer = undefined;
    this.contractForm.controls['testbed'].setValue(null);
    this.contractForm.controls['contractReferent'].setValue(null);

    this.contractForm.controls['testbed'].markAsUntouched();
    this.contractForm.controls['contractReferent'].markAsUntouched();

    this.filterTestbedsForCompany();
    this.filterCustomersForCompany();

    this.contractForm.updateValueAndValidity();
    this.changeDecRef.detectChanges();
  }

  saveContract(): void {
    this.saving = true;
    console.log('selectedCustomer:', this.selectedCustomer);
    console.log('isSameReferent:', this.contractForm.controls['isSameReferent'].value);
    const newContract = merge(new Contract(), this._contract, this.contractForm.value);
    newContract.contractReferent = this.selectedCustomer;
    newContract.company = this.selectedCompany;
    if (this.contractForm.controls['isSameReferent'].value) {
      
      const referent = (this.selectedCustomer?.firstName ? this.selectedCustomer : this.loggedUser);
      newContract.testbedReferentFirstName = referent?.firstName;
      newContract.testbedReferentLastName = referent?.lastName;
      newContract.testbedReferentEmail = referent?.email;
      newContract.testbedReferentPhoneNumber = referent?.phone;
      newContract.contractReferent = referent;
    } else if ( this._contract.id && this._contract.status === this.contractStatuses.new) {
      this.usersService.getUser(this.selectedCustomer as unknown as number).subscribe(contractReferent => {
      const referent = contractReferent?.firstName ? contractReferent : this.loggedUser;
        newContract.testbedReferentFirstName = referent?.firstName;
        newContract.testbedReferentLastName = referent?.lastName;
        newContract.testbedReferentEmail = referent?.email;
        newContract.testbedReferentPhoneNumber = referent?.phone;
        newContract.contractReferent = referent;
        this.createContract(newContract);
      });
    }

    this.generalError = undefined;

    if (this._contract.id) {
      
      switch (this._contract.status) {

        /*case this.contractStatuses.new:{
          console.log('Updating contract in NEW status');
          console.log('New contract data:', newContract);
          this.createContract(newContract);
          break;
        }*/
        case this.contractStatuses.testbedAssociation:
          this.associateTestbed(newContract.company.id, newContract.id, newContract);
          break;

        case this.contractStatuses.boxesWaiting:

          this.associateBoxes(newContract.company.id, newContract.boxes, newContract, newContract.id);
          break;

        case this.contractStatuses.ongoing:
        case this.contractStatuses.extended:
          const patchContract = new PatchContract();
          patchContract.extendedEndDate = newContract.extendedEndDate;
          this.editContract(patchContract, newContract?.id);
          break;
      }

    } else
      this.createContract(newContract);
  }

  editContract(contract: Contract, contractId: number): void {

    this.contractService.updateContract(contract, contractId)
      .subscribe(
        () => {
          this.saving = false;
          this.contract = contract;
          this.snackService.success('Contract updated!');
          this.saved.emit();
        },
        error => {
          this.saving = false;
          this.snackService.error(error.message);
          this.generalError = error;
        });
  }

  associateTestbed(companyId: number, contractId: number, newContract: Contract | any): void {

    this.testbedsService.associateTestbed(companyId, newContract.testbed, contractId)
      .subscribe(() => {
          this.saving = false;
          this.contract = newContract;
          this.snackService.success('Contract updated!');
          this.saved.emit();
        },
        error => {
          this.saving = false;
          this.snackService.error(error.message);
          this.generalError = error;
        });
  }

  associateBoxes(companyId: number, boxes: Box[], newContract: Contract, contractId?: number): void {
    this.boxesService.associateBoxes(companyId, boxes, contractId).subscribe(
      () => {
        this.saving = false;
        this.contract = newContract;
        this.snackService.success('Contract updated!');
        this.saved.emit();
      },
      error => {
        this.saving = false;
        this.snackService.error(error.message);
        this.generalError = error;
      }
    );
  }

  filterTestbedsForCompany(): void {
    if (this.selectedCompany) {
      this.filteredTestbeds = [];
      this.filteredTestbeds = this.alwaysContainsAllTestbeds;
      this.filteredTestbeds = this.filteredTestbeds.filter(testbed => testbed.company?.id === this.selectedCompany?.id && !this.isTestbedBusy(testbed));
    } else {
      this.filteredTestbeds = this.alwaysContainsAllTestbeds;
    }
  }

  filterCustomersForCompany(): void {
    if (this.iAmAdmin && this.selectedCompany) {
      this.loadingCustomers = true;
      this.customers = []; // Clear stale list immediately

      this.usersService.getAllUsersByCompany(this.selectedCompany.id).pipe(
        map((users: User[]) => users.filter(u => hasPermission(u, 'customer')))
        ).subscribe(customers => {
          this.customers = customers;
          this.loadingCustomers = false;
          this.adminHasSelectedCompany = true;
          this.changeDecRef.detectChanges();
        });
    }
  }

  createContract(newContract: Contract): void {

    if (this.iAmAdmin && newContract.testbed && !newContract.testbed.storage)
      this.dialogService.confirm("Contextual storage creation", `Do you want to create a storage for the testbed ${newContract.testbed.identifier}?`)
        .pipe(
          switchMap((value) => value ? this.dialog.open(CreateStorageDialogComponent, {
            data: {
              testbed: newContract.testbed,
              storage: new Storage()
            }
          }).afterClosed() : of(true)),
          switchMap(() => this.contractService.createContract(newContract))
        ).subscribe(
        () => {
          this.saving = false;
          this.contract = newContract;
          this.snackService.success('Contract created!');
          this.saved.emit();
        },
        error => {
          this.saving = false;
          this.snackService.error(error.message);
          this.generalError = error;
        });

    else
      this.contractService.createContract(newContract)
        .subscribe(
          () => {
            this.saving = false;
            this.contract = newContract;
            this.snackService.success('Contract created!');
            this.saved.emit();
          },
          error => {
            this.saving = false;
            this.snackService.error(error.message);
            this.generalError = error;
          });


  }

  filterBusyTestbeds(testbeds: Testbed[]): Testbed[] {
    testbeds.forEach(testbed => testbeds = this.isTestbedBusy(testbed) ? testbeds.filter(value => value.id !== testbed.id) : testbeds);
    return testbeds;
  }

  isTestbedBusy(testbed: Testbed): boolean {
    if ((testbed.contracts && testbed.contracts.length !== 0) && (testbed.id !== this._contract?.testbed?.id)) {
      return !!testbed.contracts.find(contract => contract.status === Constants.contractStatuses.new
        || contract.status === Constants.contractStatuses.approvalWaiting
        || contract.status === Constants.contractStatuses.testbedAssociation
        || contract.status === Constants.contractStatuses.boxesWaiting
        || contract.status === Constants.contractStatuses.ongoing
        || contract.status === Constants.contractStatuses.extended);
    }
    return false;
  }

  setContractType($event: MatRadioChange): void {
    this.contractType = $event.value;
    const control: AbstractControl = this.contractForm.controls['thirdPartyContract'];

    control.clearValidators();

    if (this.contractType == this.contractTypes.partnership) {
      const validatorForThirdParty: ValidatorFn[] = [Validators.required, Validators.min(this.general.minimumThirdPartyContractValue), Validators.max(this.general.maximumThirdPartyContractValue)];
      control.setValidators(validatorForThirdParty);
    }

    this.contractForm.controls['thirdPartyContract'].setValue(null);
    this.changeDecRef.detectChanges();

  }

  setTestbedReferentFieldsValidators(): void {
    const isSameReferent = this.contractForm.controls['isSameReferent'].value;
    this.testbedReferentFieldsHidden = isSameReferent;

    this.clearTestbedReferentValidators();

    if (!isSameReferent) {
      this.contractForm.controls['testbedReferentFirstName'].setValidators([Validators.required]);
      this.contractForm.controls['testbedReferentLastName'].setValidators([Validators.required]);
      this.contractForm.controls['testbedReferentEmail'].setValidators([Validators.required, Validators.pattern("[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?")]);
      this.contractForm.controls['testbedReferentPhoneNumber'].setValidators([Validators.required, Validators.pattern('[- +()0-9]+')]);
    }

    this.contractForm.controls['testbedReferentFirstName'].setValue(null);
    this.contractForm.controls['testbedReferentLastName'].setValue(null);
    this.contractForm.controls['testbedReferentEmail'].setValue(null);
    this.contractForm.controls['testbedReferentPhoneNumber'].setValue(null);

    this.changeDecRef.detectChanges();
  }

  private clearTestbedReferentValidators(): void {
    this.contractForm.controls['testbedReferentFirstName'].clearValidators();
    this.contractForm.controls['testbedReferentLastName'].clearValidators();
    this.contractForm.controls['testbedReferentEmail'].clearValidators();
    this.contractForm.controls['testbedReferentPhoneNumber'].clearValidators();
  }
}
